/**
 * EmployeeGroupedTaskView
 *
 * Group tasks by employee and bifurcate each employee's bucket into
 *   Pending · Overdue · Completed
 *
 * Used on Execution & Performance boards. The 'Overdue' bucket is
 * computed live from `is_overdue` + `delivery_date < today`, so it
 * auto-rolls forward without server intervention.
 */
import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle, Clock, CheckCircle2, ExternalLink } from 'lucide-react';

const PRIORITY_COLORS = {
  High: 'bg-rose-500/10 text-rose-400',
  Medium: 'bg-amber-500/10 text-amber-400',
  Low: 'bg-slate-500/10 text-slate-400',
};

function isOverdueTask(t, today) {
  if (t.status === 'Completed') return false;
  if (t.is_overdue) return true;
  if (t.delivery_date && t.delivery_date < today) return true;
  return false;
}

function getAssigneeIds(t) {
  if (t.assignee_ids?.length > 0) return t.assignee_ids;
  if (t.assigned_to) return [t.assigned_to];
  return ['__unassigned__'];
}

function getAssigneeNames(t) {
  if (t.assignee_names?.length > 0) return t.assignee_names;
  if (t.assigned_name) return [t.assigned_name];
  return ['Unassigned'];
}

export default function EmployeeGroupedTaskView({ tasks, onSelect, testIdPrefix = 'task' }) {
  const today = new Date().toISOString().slice(0, 10);
  const [collapsed, setCollapsed] = useState({});

  const byEmployee = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      const ids = getAssigneeIds(t);
      const names = getAssigneeNames(t);
      ids.forEach((id, idx) => {
        const name = names[idx] || names[0] || 'Unassigned';
        if (!map[id]) map[id] = { id, name, pending: [], overdue: [], completed: [] };
        if (t.status === 'Completed') map[id].completed.push(t);
        else if (isOverdueTask(t, today)) map[id].overdue.push(t);
        else map[id].pending.push(t);
      });
    });
    // Sort by overdue desc, then pending desc
    return Object.values(map).sort((a, b) => {
      if (b.overdue.length !== a.overdue.length) return b.overdue.length - a.overdue.length;
      return b.pending.length - a.pending.length;
    });
  }, [tasks, today]);

  if (byEmployee.length === 0) {
    return <div className="glass-card p-12 text-center text-slate-500" data-testid={`${testIdPrefix}-empty`}>No tasks found</div>;
  }

  return (
    <div className="space-y-3" data-testid={`${testIdPrefix}-grouped`}>
      {byEmployee.map(emp => (
        <EmployeeGroup key={emp.id} emp={emp}
          collapsed={!!collapsed[emp.id]}
          onToggle={() => setCollapsed({ ...collapsed, [emp.id]: !collapsed[emp.id] })}
          onSelect={onSelect}
          testIdPrefix={testIdPrefix} />
      ))}
    </div>
  );
}

function EmployeeGroup({ emp, collapsed, onToggle, onSelect, testIdPrefix }) {
  const total = emp.pending.length + emp.overdue.length + emp.completed.length;
  return (
    <div className="glass-card overflow-hidden" data-testid={`${testIdPrefix}-emp-${emp.id}`}>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.02] transition-colors" data-testid={`${testIdPrefix}-emp-toggle-${emp.id}`}>
        <div className="flex items-center gap-3">
          {collapsed ? <ChevronRight size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-400" />}
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-600/30 to-purple-600/30 flex items-center justify-center text-xs font-bold text-white">
            {emp.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div className="text-left">
            <div className="text-sm text-white font-semibold">{emp.name}</div>
            <div className="text-[10px] text-slate-500">{total} task{total !== 1 ? 's' : ''}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          {emp.overdue.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">
              <AlertTriangle size={11} /> {emp.overdue.length} Overdue
            </span>
          )}
          {emp.pending.length > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
              <Clock size={11} /> {emp.pending.length} Pending
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <CheckCircle2 size={11} /> {emp.completed.length} Done
          </span>
        </div>
      </button>

      {!collapsed && (
        <div className="border-t border-white/5 px-4 py-3 space-y-3 bg-black/20">
          {emp.overdue.length > 0 && (
            <Bucket title="Overdue" tone="rose" tasks={emp.overdue} onSelect={onSelect} testIdPrefix={`${testIdPrefix}-${emp.id}-overdue`} />
          )}
          {emp.pending.length > 0 && (
            <Bucket title="Pending" tone="amber" tasks={emp.pending} onSelect={onSelect} testIdPrefix={`${testIdPrefix}-${emp.id}-pending`} />
          )}
          {emp.completed.length > 0 && (
            <Bucket title="Completed" tone="emerald" tasks={emp.completed} onSelect={onSelect} testIdPrefix={`${testIdPrefix}-${emp.id}-completed`} />
          )}
          {total === 0 && <div className="text-center text-xs text-slate-600 py-3">No tasks</div>}
        </div>
      )}
    </div>
  );
}

function Bucket({ title, tone, tasks, onSelect, testIdPrefix }) {
  const toneClasses = {
    rose: 'text-rose-400',
    amber: 'text-amber-400',
    emerald: 'text-emerald-400',
  };
  return (
    <div data-testid={`${testIdPrefix}-bucket`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold mb-2 ${toneClasses[tone]}`}>
        {title} <span className="text-slate-500 font-normal">({tasks.length})</span>
      </div>
      <div className="space-y-1">
        {tasks.map(t => (
          <button key={t.task_id} onClick={() => onSelect(t)}
            className="w-full text-left flex items-center gap-3 px-3 py-2 rounded bg-white/[0.02] hover:bg-white/[0.05] transition-colors border border-white/5"
            data-testid={`${testIdPrefix}-row-${t.task_id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-xs text-white truncate">{t.client_name}</span>
                <span className="text-[10px] text-slate-500 truncate">· {t.task_type}</span>
              </div>
              {t.description && <div className="text-[10px] text-slate-500 truncate mt-0.5">{t.description}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {t.priority && <span className={`text-[10px] px-1.5 py-0.5 rounded ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>}
              <span className="text-[10px] text-slate-500 font-mono">{t.delivery_date || t.task_date || '—'}</span>
              <ExternalLink size={11} className="text-slate-600" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
